import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  IconButton,
  Paper,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { toBlob } from 'html-to-image';
import moment from 'moment';
import HourRangePicker, {
  EMPTY_RANGE,
  HourRange,
  cloneRange,
  formatDurationFromMinutes,
  getShiftMinutes,
  hasDayEntry,
} from './HourRangePicker';
import ShiftChangeHistory, { ShiftChange } from './ShiftChangeHistory';
import ScheduleExport from './ScheduleExport';
import { useStyles } from './ScheduleStyles';
import { loadScheduleState, saveScheduleState } from '../../storage/scheduleStore';
import { useLocale } from '../../i18n/LocaleContext';

const PersonIcon = () => (
  <SvgIcon>
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </SvgIcon>
);

const PEOPLE = ['Ade', 'Cera', 'Kati', 'Andreea'];
const WORKDAY_HOURS = 8;
const WORKDAYS_PER_WEEK = 5;
const WORKDAY_MINUTES = WORKDAY_HOURS * 60;

const getWeekDays = (weekStart: moment.Moment) =>
  Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'days'));

const getShiftKey = (person: string, dayKey: string) => `${person}-${dayKey}`;

const getMonthCapacityMinutes = (monthKey: string) => {
  const start = moment(`${monthKey}-01`);
  const daysInMonth = start.daysInMonth();
  let workdays = 0;

  for (let day = 1; day <= daysInMonth; day += 1) {
    if (start.clone().date(day).isoWeekday() <= WORKDAYS_PER_WEEK) {
      workdays += 1;
    }
  }

  return workdays * WORKDAY_MINUTES;
};

const getUnavailableMinutes = (range: HourRange) => {
  if (range.mark === 'off') return 0;
  if (range.mark === 'holiday') return WORKDAY_MINUTES;

  return getShiftMinutes(range);
};

const formatRemainingHours = (
  remainingMinutes: number,
  t: (key: 'hoursLeft' | 'hoursOver', vars: { hours: string }) => string,
) => {
  if (remainingMinutes >= 0) {
    return t('hoursLeft', {
      hours: formatDurationFromMinutes(remainingMinutes),
    });
  }

  return t('hoursOver', {
    hours: formatDurationFromMinutes(-remainingMinutes),
  });
};

type SwapSelection = {
  person: string;
  dayKey: string;
};

const Schedule = () => {
  const classes = useStyles();
  const { locale, t } = useLocale();
  const [ready, setReady] = useState(false);
  const [weekStart, setWeekStart] = useState(() =>
    moment().startOf('isoWeek'),
  );
  const [shifts, setShifts] = useState<Record<string, HourRange>>({});
  const [changes, setChanges] = useState<ShiftChange[]>([]);
  const [swapMode, setSwapMode] = useState(false);
  const [swapSelection, setSwapSelection] = useState<SwapSelection | null>(
    null,
  );
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    loadScheduleState()
      .then((state) => {
        if (cancelled) return;
        setShifts(state.shifts);
        setChanges(state.changes);
        setReady(true);
      })
      .catch((error) => {
        console.error('Failed to load schedule', error);
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const days = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const todayKey = moment().format('YYYY-MM-DD');
  const weekLabel = `${days[0].clone().locale(locale).format('D MMM')} – ${days[6].clone().locale(locale).format('D MMM YYYY')}`;

  const visibleMonths = useMemo(() => {
    const years = new Set(days.map((day) => day.year()));
    const months: { key: string; label: string; shortLabel: string }[] = [];

    days.forEach((day) => {
      const key = day.format('YYYY-MM');
      if (months.some((month) => month.key === key)) return;

      months.push({
        key,
        label:
          years.size > 1
            ? day.clone().locale(locale).format('MMM YYYY')
            : day.clone().locale(locale).format('MMMM'),
        shortLabel:
          years.size > 1
            ? day.clone().locale(locale).format('MMM YYYY')
            : day.clone().locale(locale).format('MMM'),
      });
    });

    return months;
  }, [days, locale]);

  const monthlyTotals = useMemo(() => {
    const totals = Object.fromEntries(
      visibleMonths.map((month) => [
        month.key,
        Object.fromEntries(PEOPLE.map((person) => [person, 0])),
      ]),
    ) as Record<string, Record<string, number>>;

    Object.entries(shifts).forEach(([shiftKey, range]) => {
      const minutes = getUnavailableMinutes(range);
      if (minutes <= 0) return;

      const person = PEOPLE.find((name) => shiftKey.startsWith(`${name}-`));
      if (!person) return;

      const dayKey = shiftKey.slice(person.length + 1);
      const monthKey = dayKey.slice(0, 7);
      if (!totals[monthKey]) return;

      totals[monthKey][person] += minutes;
    });

    return totals;
  }, [shifts, visibleMonths]);

  const monthlyRemaining = useMemo(() => {
    const remaining = Object.fromEntries(
      visibleMonths.map((month) => [
        month.key,
        Object.fromEntries(
          PEOPLE.map((person) => [person, getMonthCapacityMinutes(month.key)]),
        ),
      ]),
    ) as Record<string, Record<string, number>>;

    Object.entries(shifts).forEach(([shiftKey, range]) => {
      const person = PEOPLE.find((name) => shiftKey.startsWith(`${name}-`));
      if (!person) return;

      const dayKey = shiftKey.slice(person.length + 1);
      const monthKey = dayKey.slice(0, 7);
      if (!remaining[monthKey]) return;

      remaining[monthKey][person] -= getUnavailableMinutes(range);
    });

    return remaining;
  }, [shifts, visibleMonths]);

  const getColumnClass = (day: moment.Moment) => {
    const dayKey = day.format('YYYY-MM-DD');
    if (dayKey === todayKey) return classes.todayColumn;
    if (day.isoWeekday() >= 6) return classes.weekendColumn;
    return undefined;
  };

  useEffect(() => {
    if (!ready) return;
    saveScheduleState({ shifts, changes });
  }, [shifts, changes, ready]);

  const updateShift = (person: string, dayKey: string, next: HourRange) => {
    const shiftKey = getShiftKey(person, dayKey);
    setShifts((prev) => ({ ...prev, [shiftKey]: next }));
  };

  const applyRange = (
    next: Record<string, HourRange>,
    person: string,
    dayKey: string,
    range: HourRange,
  ) => {
    const key = getShiftKey(person, dayKey);
    if (hasDayEntry(range)) {
      next[key] = cloneRange(range);
    } else {
      delete next[key];
    }
  };

  const cancelSwap = () => {
    setSwapMode(false);
    setSwapSelection(null);
  };

  const swapShifts = (first: SwapSelection, second: SwapSelection) => {
    const keyA = getShiftKey(first.person, first.dayKey);
    const keyB = getShiftKey(second.person, second.dayKey);
    const rangeA = cloneRange(shifts[keyA] ?? EMPTY_RANGE);
    const rangeB = cloneRange(shifts[keyB] ?? EMPTY_RANGE);

    setShifts((prev) => {
      const next = { ...prev };
      applyRange(next, first.person, first.dayKey, rangeB);
      applyRange(next, second.person, second.dayKey, rangeA);
      return next;
    });

    setChanges((prev) => [
      {
        id: crypto.randomUUID(),
        at: new Date().toISOString(),
        personA: first.person,
        dayA: first.dayKey,
        rangeA,
        personB: second.person,
        dayB: second.dayKey,
        rangeB,
      },
      ...prev,
    ]);

    cancelSwap();
  };

  const undoChange = (change: ShiftChange) => {
    setShifts((prev) => {
      const next = { ...prev };
      applyRange(next, change.personA, change.dayA, change.rangeA);
      applyRange(next, change.personB, change.dayB, change.rangeB);
      return next;
    });
    setChanges((prev) => prev.filter((item) => item.id !== change.id));
  };

  const handleSwapSelect = (person: string, dayKey: string) => {
    if (!swapSelection) {
      setSwapSelection({ person, dayKey });
      return;
    }

    if (swapSelection.person === person && swapSelection.dayKey === dayKey) {
      setSwapSelection(null);
      return;
    }

    swapShifts(swapSelection, { person, dayKey });
  };

  const swapHint = (() => {
    if (!swapMode) return null;
    if (!swapSelection) {
      return t('swapHintFirst');
    }

    return t('swapHintSecond', {
      person: swapSelection.person,
      date: moment(swapSelection.dayKey).locale(locale).format('ddd D MMM'),
    });
  })();

  const exportToPng = async () => {
    const node = exportRef.current;
    if (!node || exporting) return;

    setExporting(true);

    try {
      const blob = await toBlob(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        skipFonts: true,
      });

      if (!blob) {
        throw new Error('Schedule image could not be created');
      }

      const filename = `schedule-${days[0].format('YYYY-MM-DD')}-to-${days[6].format('YYYY-MM-DD')}.png`;
      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: t('schedule') });
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
      console.error('Failed to export schedule', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
      <div className={classes.page}>
        {!ready ? (
          <Typography variant="body1" color="text.secondary">
            {t('loading')}
          </Typography>
        ) : (
          <>
        <div className={classes.header}>
          <div className={classes.titleBlock}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
            >
              {t('schedule')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {weekLabel}
            </Typography>
          </div>
          <div className={classes.headerActions}>
            <IconButton
              component={Link}
              to="/profile"
              aria-label={t('profile')}
              className={classes.profileButton}
            >
              <PersonIcon />
            </IconButton>
            <div className={classes.weekNav}>
              <Button
                variant="outlined"
                color={swapMode ? 'error' : 'secondary'}
                onClick={() => (swapMode ? cancelSwap() : setSwapMode(true))}
              >
                {swapMode ? t('cancelSwap') : t('swapShifts')}
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={exportToPng}
                disabled={exporting}
              >
                {exporting ? t('exporting') : t('exportPng')}
              </Button>
            </div>
          </div>
        </div>

        {swapHint && (
          <Typography variant="body2" className={classes.swapHint}>
            {swapHint}
          </Typography>
        )}

        <TableContainer component={Paper} className={classes.tablePaper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.personCell}>{t('person')}</TableCell>
                {days.map((day) => {
                  const dayKey = day.format('YYYY-MM-DD');

                  return (
                    <TableCell
                      key={dayKey}
                      align="center"
                      className={getColumnClass(day)}
                    >
                      <div className={classes.dayHeader}>
                        <Typography variant="subtitle2">
                          {day.clone().locale(locale).format('ddd')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {day.clone().locale(locale).format('D MMM')}
                        </Typography>
                      </div>
                    </TableCell>
                  );
                })}
                {visibleMonths.map((month) => (
                  <TableCell
                    key={month.key}
                    align="center"
                    className={classes.monthHeader}
                  >
                    <div className={classes.dayHeader}>
                      <Typography variant="subtitle2">{month.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('total')}
                      </Typography>
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PEOPLE.map((person) => (
                <TableRow key={person} hover>
                  <TableCell className={classes.personCell}>
                    <div className={classes.personName}>
                      <span>{person}</span>
                      {visibleMonths.map((month) => {
                        const remaining = monthlyRemaining[month.key][person];

                        return (
                          <span
                            key={`${person}-${month.key}-remaining`}
                            className={
                              remaining > 0
                                ? `${classes.remainingHours} ${classes.remainingUnder}`
                                : remaining < 0
                                  ? `${classes.remainingHours} ${classes.remainingOver}`
                                  : classes.remainingHours
                            }
                          >
                            {visibleMonths.length > 1
                              ? `${month.shortLabel} · `
                              : ''}
                            {formatRemainingHours(remaining, t)}
                          </span>
                        );
                      })}
                    </div>
                  </TableCell>
                  {days.map((day) => {
                    const dayKey = day.format('YYYY-MM-DD');
                    const shiftKey = getShiftKey(person, dayKey);
                    const columnClass = getColumnClass(day);

                    return (
                      <TableCell
                        key={shiftKey}
                        className={
                          columnClass
                            ? `${classes.shiftCell} ${columnClass}`
                            : classes.shiftCell
                        }
                      >
                        <HourRangePicker
                          label={`${person} · ${day.clone().locale(locale).format('ddd D MMM')}`}
                          value={shifts[shiftKey] ?? EMPTY_RANGE}
                          onChange={(next) => updateShift(person, dayKey, next)}
                          selectionMode={swapMode}
                          selected={
                            swapSelection?.person === person &&
                            swapSelection.dayKey === dayKey
                          }
                          onSelect={() => handleSwapSelect(person, dayKey)}
                        />
                      </TableCell>
                    );
                  })}
                  {visibleMonths.map((month) => (
                    <TableCell
                      key={`${person}-${month.key}`}
                      align="center"
                      className={classes.monthTotalCell}
                    >
                      {formatDurationFromMinutes(
                        monthlyTotals[month.key][person],
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.weekControls}>
          <Button
            variant="outlined"
            onClick={() =>
              setWeekStart((prev) => prev.clone().subtract(1, 'week'))
            }
          >
            {t('previous')}
          </Button>
          <Button
            variant="contained"
            onClick={() => setWeekStart(moment().startOf('isoWeek'))}
          >
            {t('thisWeek')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setWeekStart((prev) => prev.clone().add(1, 'week'))}
          >
            {t('next')}
          </Button>
        </div>

        <ScheduleExport
          ref={exportRef}
          people={PEOPLE}
          days={days}
          shifts={shifts}
          locale={locale}
        />

        <ShiftChangeHistory changes={changes} onUndo={undoChange} />
          </>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default Schedule;
